import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import { DIAL_CODES, DEFAULT_DIAL } from '../data/countryDialCodes';

/**
 * Phone number field with a searchable country dial-code selector.
 * Emits the combined value ("1868 3751234", no leading "+") via onChange, or
 * "" when the number is empty. The "+" is deliberately never sent — Google
 * Sheets treats a cell starting with "+" (like "=") as the start of a
 * formula, which corrupted early registrations' phone numbers.
 */
export default function PhoneInput({ label, value = '', onChange, required = false }) {
  const [dial, setDial] = useState(DEFAULT_DIAL);
  const [number, setNumber] = useState('');
  const initialized = useRef(false);

  // Parse an incoming value once (supports prefill/edit). Accepts the dial
  // code with or without a leading "+" so both old ("+1868 ...") and new
  // ("1868 ...") stored values still populate the selector correctly.
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (value) {
      const parts = String(value).trim().split(/\s+/);
      const firstBare = (parts[0] || '').replace(/^\+/, '');
      if (firstBare) {
        // longest matching dial prefix wins (1868 before 1)
        const match = [...DIAL_CODES]
          .sort((a, b) => b.dial.length - a.dial.length)
          .find((c) => firstBare === c.dial.replace(/^\+/, ''));
        if (match) {
          setDial(match);
          setNumber(parts.slice(1).join(' '));
          return;
        }
      }
      setNumber(String(value));
    }
  }, [value]);

  const emit = (d, n) => {
    const trimmed = (n || '').trim();
    const dialDigits = d.dial.replace(/^\+/, ''); // strip "+" before sending anywhere
    onChange(trimmed ? `${dialDigits} ${trimmed}` : '');
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Autocomplete
        options={DIAL_CODES}
        value={dial}
        disableClearable
        openOnFocus
        autoHighlight
        isOptionEqualToValue={(a, b) => a.code === b.code}
        getOptionLabel={(o) => `${o.flag} ${o.dial}`}
        filterOptions={(opts, { inputValue }) => {
          const q = inputValue.trim().toLowerCase();
          if (!q) return opts;
          return opts.filter(
            (o) =>
              o.name.toLowerCase().includes(q) ||
              o.dial.includes(q) ||
              o.code.toLowerCase().includes(q)
          );
        }}
        renderOption={(props, o) => (
          <li {...props} key={o.code}>
            <span style={{ marginRight: 8 }}>{o.flag}</span>
            {o.name}&nbsp;<span style={{ color: '#666' }}>({o.dial})</span>
          </li>
        )}
        onChange={(e, v) => {
          if (v) {
            setDial(v);
            emit(v, number);
          }
        }}
        sx={{ width: 140, flexShrink: 0 }}
        renderInput={(params) => <TextField {...params} label="Code" />}
      />
      <TextField
        fullWidth
        required={required}
        label={label}
        value={number}
        onChange={(e) => {
          setNumber(e.target.value);
          emit(dial, e.target.value);
        }}
        placeholder="375 1234"
      />
    </Box>
  );
}
